import React from 'react';
import { STATUS_COLORS, STATUS_LABELS, toInitCap } from './weatherConstants';
import { formatHarvestDelta } from './harvestAlertUtils';

export const RegionPopupContent = ({ region, isDark }) => {
  // Two harvest seasons (double-crop): backend may send seasons[]; fall back to the single window.
  const seasons = Array.isArray(region.seasons) && region.seasons.length > 1 ? region.seasons : null;
  // Season alert: prefer the normalized `alert` object; fall back to flat season_alert_* fields.
  const alert = region.alert?.active
    ? region.alert
    : (region.season_alert_active
        ? {
            active: true,
            message: region.season_alert,
            weeks_delta: region.season_alert_weeks_delta,
          }
        : null);
  const labelCls = isDark ? 'text-gray-400' : 'text-[#828382]';
  const valueCls = isDark ? 'text-white' : 'text-[#282A29]';
  // "3 weeks ahead / behind" — computed from the signed week delta (shared with the panel).
  const deltaPhrase = formatHarvestDelta(alert?.weeks_delta);
  // New (shifted) season start once an alert has moved the window (ISO week only, no date).
  const newStartWeek = region.seasons?.[0]?.iso_week_start ?? null;

  return (
  <div className="min-w-[200px]">
    <div className={`font-semibold text-base mb-2 ${valueCls}`}>
      {toInitCap(region.specific_location || region.region)}
    </div>
    <div className="text-sm space-y-1">
      <div>
        <span className={labelCls}>Commodity:</span>{' '}
        <span className={valueCls}>
          {toInitCap(region.commodityKey || region.commodity)}
        </span>
      </div>
      <div>
        <span className={labelCls}>Region:</span>{' '}
        <span className={valueCls}>
          {toInitCap(region.region)}, {toInitCap(region.country)}
        </span>
      </div>
      <div>
        <span className={labelCls}>Status:</span>
        <span style={{ color: region.harvest_status === 'not_producing' ? 'var(--harvest-not-producing-text, #6B7280)' : STATUS_COLORS[region.harvest_status], marginLeft: 4 }}>
          {STATUS_LABELS[region.harvest_status]}
        </span>
      </div>
      {region.harvest_window &&
        String(region.harvest_window).replace(/n\/a/gi, '').replace(/[\s,\-]/g, '') !== '' && (
      seasons ? (
        <div>
          <span className={labelCls}>Harvest:</span>
          <div className="mt-0.5 space-y-0.5">
            {seasons.map((s, i) => (
              <div key={`${s.harvest_window || ''}-${s.harvest_status || ''}-${i}`} className={valueCls}>
                {s.harvest_window}
                {s.harvest_status && (
                  <span style={{ color: s.harvest_status === 'not_producing' ? 'var(--harvest-not-producing-text, #6B7280)' : STATUS_COLORS[s.harvest_status], marginLeft: 6 }}>
                    · {STATUS_LABELS[s.harvest_status]}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <span className={labelCls}>Harvest:</span>{' '}
          <span className={valueCls}>{region.harvest_window}</span>
        </div>
      ))}
      {alert?.active && (
        <div
          className="mt-1.5 rounded-md px-2 py-1.5"
          style={{
            background: isDark ? 'rgba(245,158,11,0.15)' : '#fef3c7',
            border: '1px solid #f59e0b',
          }}
        >
          <div className="flex items-start gap-1.5">
            <span aria-hidden style={{ color: '#f59e0b', lineHeight: '1.2' }}>⚠</span>
            <div className="text-xs">
              <div className={`font-medium ${isDark ? 'text-amber-200' : 'text-[#92400e]'}`}>
                {deltaPhrase}{alert.message ? ` — ${alert.message}` : ''}
              </div>
              {newStartWeek != null && (
                <div className={isDark ? 'text-gray-400' : 'text-[#a16207]'}>
                  New start: Week {newStartWeek}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {region.predominant_varieties &&
        String(region.predominant_varieties).trim().toLowerCase() !== 'n/a' && (
        <div>
          <span className={isDark ? 'text-gray-400' : 'text-[#828382]'}>Varieties:</span>{' '}
          <span className={isDark ? 'text-white' : 'text-[#282A29]'}>
            {region.predominant_varieties}
          </span>
        </div>
      )}
    </div>
  </div>
  );
};
